import { FileProcessingResultStatus } from '@atlas/types'
import type { ConcernState } from '@atlas/ui-components'
import { logger } from '@/lib/logger'
import type { FileDTO } from '@/services/file.service'
import type { UploadItem } from './upload-item.types'
import { canReleaseWithoutThumbnail } from './upload-status'

const log = logger.child('upload-store')

/**
 * The store state this module reads and writes, handed in rather than imported.
 *
 * Everything here is about reconciling one item against the server's answer for
 * its File; none of it is about owning the queue. Passing the two operations it
 * needs keeps that boundary explicit and keeps `upload-store` about queue state,
 * the same way the callback registry and the summary already do.
 */
export interface RefreshHost {
	items: () => UploadItem[]
	patchItem: (id: string, patch: Partial<UploadItem>) => void
}

/**
 * Per-file counter making `refreshItemFromServer` last-issued-wins.
 *
 * Keyed by server `fileId` rather than item id: the refresh callers only ever
 * hold the former.
 */
const refreshSequence = new Map<string, number>()

/** Test-only: drop the sequence counters so one spec's in-flight refreshes cannot leak into the next. */
export function clearRefreshSequences(): void {
	refreshSequence.clear()
}

/** Maps the API's `processingResults` block onto the tile-facing concern map. */
export function toConcerns(file: FileDTO): Record<string, ConcernState> {
	const results = file.processingResults ?? {}
	const concerns: Record<string, ConcernState> = {}
	for (const [id, entry] of Object.entries(results)) {
		concerns[id] = { status: entry.status, errorMessage: entry.errorMessage }
	}
	return concerns
}

/** Puts a single concern back to pending after a retry was accepted, leaving siblings untouched. */
export function markConcernPending(host: RefreshHost, id: string, concernId: string): void {
	const item = host.items().find((candidate) => candidate.id === id)
	if (!item || !(concernId in item.concerns)) return
	host.patchItem(id, {
		concerns: { ...item.concerns, [concernId]: { status: FileProcessingResultStatus.PENDING } },
	})
}

/** The slice of `fileService` the store needs, so tests can pass a stub. */
export interface UploadFileGateway {
	getFile: (fileId: string) => Promise<FileDTO>
	reprocessConcern: (fileId: string, concernId: string) => Promise<void>
	getThumbnailUrl?: (fileId: string) => Promise<{ thumbnailUrl: string }>
}

/**
 * Refetches one file and replaces its concern map from the response.
 *
 * The single path both the push handler and the poll fallback take, so the two
 * are idempotent with respect to each other — whichever arrives second simply
 * re-reads the same server state. Deliberately reads nothing from the event
 * payload: only `GET /files/{id}` is authoritative about concern state.
 *
 * Responses are applied IN ORDER OF ISSUE, not arrival. Four callers can have
 * requests in flight for the same file at once, and concern state only moves
 * forward — so a slow response landing after a newer one would demote a SETTLED
 * item back to CONFIRMED, flipping the tile back to "Classifying…", restoring
 * the status-bar chip and re-arming the unload guard. Being idempotent with
 * respect to identical responses is not the same as being safe out of order.
 */
export async function refreshItemFromServer(
	host: RefreshHost,
	fileId: string,
	gateway: UploadFileGateway,
): Promise<void> {
	const item = host.items().find((candidate) => candidate.fileId === fileId)
	if (!item) return
	const sequence = (refreshSequence.get(fileId) ?? 0) + 1
	refreshSequence.set(fileId, sequence)
	try {
		const file = await gateway.getFile(fileId)
		log.debug('refreshed from server', {
			fileId,
			concerns: Object.fromEntries(
				Object.entries(file.processingResults ?? {}).map(([id, entry]) => [id, entry.status]),
			),
			thumbnailStatus: file.thumbnailStatus,
		})
		// Re-resolve the item: an await means the array may have been replaced, and
		// the item may even have been removed while the request was in flight.
		if (!host.items().some((candidate) => candidate.id === item.id)) return
		// A newer request has already answered; this one is stale.
		if (refreshSequence.get(fileId) !== sequence) {
			log.debug('discarded a stale refresh', { fileId, sequence })
			return
		}
		host.patchItem(item.id, { concerns: toConcerns(file) })

		// Swap the local blob preview for the server-rendered thumbnail once one
		// exists — it reflects what the library will actually show, and it frees
		// the object URL pinning the original file in memory.
		const current = host.items().find((candidate) => candidate.id === item.id)
		if (file.thumbnailStatus === 'completed' && !current?.previewUrl && gateway.getThumbnailUrl) {
			const { thumbnailUrl } = await gateway.getThumbnailUrl(fileId)
			// Drop the local File in the same patch: the server thumbnail now backs
			// the tile, so holding the original bytes only pins them in memory for
			// the rest of the session. Released here rather than at confirm, so the
			// preview never blanks while the thumbnail is still rendering.
			if (host.items().some((candidate) => candidate.id === item.id)) {
				host.patchItem(item.id, { previewUrl: thumbnailUrl, file: undefined })
			}
		} else if (current && canReleaseWithoutThumbnail(file.thumbnailStatus, current)) {
			host.patchItem(item.id, { file: undefined })
		}
	} catch {
		// A failed refresh leaves the last-known state in place; the next push or
		// poll tick tries again. Surfacing it would be noise — the tile is still
		// showing something truthful.
	}
}

/**
 * Retries one concern and optimistically reopens it locally, so the tile stops
 * showing a failure the user has already acted on. The server's own answer
 * arrives through the same refresh path as the first attempt.
 */
export async function retryConcern(
	host: RefreshHost,
	id: string,
	concernId: string,
	gateway: UploadFileGateway,
): Promise<void> {
	const item = host.items().find((candidate) => candidate.id === id)
	if (!item?.fileId) return
	await gateway.reprocessConcern(item.fileId, concernId)
	markConcernPending(host, id, concernId)
}
